import { useEffect, useMemo, useState } from "react";
import { env } from "../../config/env";

/**
 * CategorySelect — single source of truth for the primary-category dropdown
 * used by Create Deal AND Edit Deal flows.
 *
 * Why this is its own component:
 *  - Consistency: both pages must show the same active, non-promotional
 *    categories grouped by display_group. Diverging copies caused bugs
 *    where MyInventory accepted free-text values that the typed-deals
 *    backend later rejected with `category_lookup_miss`.
 *  - Loads the live taxonomy from `/api/v2/categories?promotional=false` so
 *    runtime category additions appear without a frontend deploy.
 *  - Rejects promotional categories at the source so the seller can never
 *    pick one as a primary slug.
 */

export interface CategoryOption {
  slug: string;
  name: string;
  group: string;        // display_group label
  group_slug: string;
  item_types: string[];
}

interface RawCategoryV2 {
  slug?: string;
  name?: string;
  display_group?: string;
  group_slug?: string;
  item_types?: unknown;
  is_promotional?: boolean;
  is_active?: boolean;
}

let _cachedCategories: CategoryOption[] | null = null;
let _cachePromise: Promise<CategoryOption[]> | null = null;

async function loadCategoriesOnce(): Promise<CategoryOption[]> {
  if (_cachedCategories) return _cachedCategories;
  if (_cachePromise) return _cachePromise;
  _cachePromise = fetch(`${env.apiBaseUrl}/api/v2/categories?promotional=false`)
    .then((r) => (r.ok ? r.json() : []))
    .then((data: unknown) => {
      const list = (Array.isArray(data)
        ? data
        : (data as { categories?: RawCategoryV2[]; items?: RawCategoryV2[] })?.categories ??
          (data as { items?: RawCategoryV2[] })?.items ??
          []) as RawCategoryV2[];
      const cats = list
        .filter((c) => c?.slug && c?.name && c?.is_active !== false && c?.is_promotional !== true)
        .map<CategoryOption>((c) => ({
          slug: String(c.slug),
          name: String(c.name),
          group: String(c.display_group ?? c.group_slug ?? ""),
          group_slug: String(c.group_slug ?? ""),
          item_types: Array.isArray(c.item_types) ? c.item_types.map(String) : [],
        }));
      _cachedCategories = cats;
      return cats;
    })
    .catch(() => {
      _cachePromise = null;
      return [];
    });
  return _cachePromise;
}

export interface CategorySelectProps {
  value: string;            // selected category slug
  onChange: (slug: string) => void;
  placeholderLabel?: string;
  emptyOptionLabel?: string;
  className?: string;
  /** Set true to show the categories grouped by their display_group via <optgroup>. */
  grouped?: boolean;
  /** Hide a specific slug (e.g. the "Let AI decide" placeholder). */
  excludeSlug?: string;
  /** Filter to a specific group_slug (rarely needed). */
  filterGroupSlug?: string;
  disabled?: boolean;
}

export function CategorySelect({
  value,
  onChange,
  placeholderLabel = "Pick a category…",
  emptyOptionLabel,
  className = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white",
  grouped = true,
  excludeSlug,
  filterGroupSlug,
  disabled = false,
}: CategorySelectProps) {
  const [options, setOptions] = useState<CategoryOption[]>(_cachedCategories ?? []);
  const [loading, setLoading] = useState<boolean>(_cachedCategories === null);

  useEffect(() => {
    let cancelled = false;
    if (_cachedCategories) {
      setOptions(_cachedCategories);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadCategoriesOnce().then((cats) => {
      if (cancelled) return;
      setOptions(cats);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    return options
      .filter((c) => (excludeSlug ? c.slug !== excludeSlug : true))
      .filter((c) => (filterGroupSlug ? c.group_slug === filterGroupSlug : true));
  }, [options, excludeSlug, filterGroupSlug]);

  const groups = useMemo(
    () => Array.from(new Set(filtered.map((c) => c.group || "Other"))),
    [filtered],
  );

  if (loading) {
    return (
      <select disabled className={className} value="">
        <option value="">Loading categories…</option>
      </select>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className}
      disabled={disabled}
    >
      <option value="">{emptyOptionLabel ?? placeholderLabel}</option>
      {grouped
        ? groups.map((group) => (
            <optgroup key={group} label={group}>
              {filtered
                .filter((c) => (c.group || "Other") === group)
                .map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
            </optgroup>
          ))
        : filtered.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
    </select>
  );
}

/** Returns the cached active non-promotional categories. Caller can await. */
export const fetchActiveCategories = loadCategoriesOnce;

/** Test-only — clear the in-process cache so a refetch happens. */
export function resetCategorySelectCache() {
  _cachedCategories = null;
  _cachePromise = null;
}
